import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IconArrowLeft, IconArrowRight, IconHelpCircle } from "@tabler/icons-react";
import { cn } from "../../utils/encoder";

export type EncoderToastTone = "error" | "success" | "info";

export type EncoderToast = {
  id: number;
  tone: EncoderToastTone;
  message: string;
};

type EncoderUiContextValue = {
  toasts: EncoderToast[];
  showToast: (message: string, tone?: EncoderToastTone) => void;
  dismissToast: (id: number) => void;
};

export type CmChromeNav = {
  canGoBack: boolean;
  canGoForward: boolean;
  onBack: () => void;
  onForward: () => void;
};

type CmChromeNavContextValue = {
  nav: CmChromeNav | null;
  setNav: (nav: CmChromeNav | null) => void;
};

const EncoderUiContext = createContext<EncoderUiContextValue | null>(null);
const CmChromeNavContext = createContext<CmChromeNavContextValue | null>(null);

export const EncoderUiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<EncoderToast[]>([]);
  const [nav, setNav] = useState<CmChromeNav | null>(null);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, tone: EncoderToastTone = "info") => {
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setToasts((current) => [...current, { id, tone, message }]);
      window.setTimeout(() => dismissToast(id), 4500);
    },
    [dismissToast]
  );

  const uiValue = useMemo(
    () => ({ toasts, showToast, dismissToast }),
    [toasts, showToast, dismissToast]
  );
  const navValue = useMemo(() => ({ nav, setNav }), [nav]);

  return (
    <EncoderUiContext.Provider value={uiValue}>
      <CmChromeNavContext.Provider value={navValue}>
        {children}
      </CmChromeNavContext.Provider>
    </EncoderUiContext.Provider>
  );
};

export const useEncoderUi = (): EncoderUiContextValue => {
  const value = useContext(EncoderUiContext);
  if (!value) {
    return {
      toasts: [],
      showToast: () => undefined,
      dismissToast: () => undefined,
    };
  }
  return value;
};

export const useRegisterCmChromeNav = ({
  canGoBack,
  canGoForward,
  onBack,
  onForward,
}: CmChromeNav) => {
  const setNav = useContext(CmChromeNavContext)?.setNav;
  const onBackRef = useRef(onBack);
  const onForwardRef = useRef(onForward);
  onBackRef.current = onBack;
  onForwardRef.current = onForward;

  useEffect(() => {
    if (!setNav) return undefined;
    setNav({
      canGoBack,
      canGoForward,
      onBack: () => onBackRef.current(),
      onForward: () => onForwardRef.current(),
    });
    return () => setNav(null);
  }, [setNav, canGoBack, canGoForward]);
};

export const CmHowItWorksControl: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const nav = useContext(CmChromeNavContext)?.nav ?? null;

  return (
    <div className="ml-1 flex shrink-0 flex-nowrap items-center gap-1.5">
      <button
        type="button"
        title="How to use the builder"
        aria-pressed={open}
        onClick={() => onOpenChange(!open)}
        className={cn(
          "inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 text-sm text-emerald-900 hover:bg-emerald-100",
          open && "border-emerald-300 bg-emerald-100"
        )}
      >
        <IconHelpCircle size={14} />
        How it works
      </button>
      {nav ? (
        <>
          <button
            type="button"
            aria-label="Back"
            title="Back"
            disabled={!nav.canGoBack}
            onClick={nav.onBack}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 disabled:opacity-40"
          >
            <IconArrowLeft size={14} />
          </button>
          <button
            type="button"
            aria-label="Forward"
            title="Forward"
            disabled={!nav.canGoForward}
            onClick={nav.onForward}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 disabled:opacity-40"
          >
            <IconArrowRight size={14} />
          </button>
        </>
      ) : null}
      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-[min(1100px,95vw)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b bg-slate-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-slate-900">
                How the ICD-10-CM Builder works
              </h2>
              <button
                type="button"
                className="h-9 rounded-md border px-4 text-sm"
                onClick={() => onOpenChange(false)}
              >
                Close
              </button>
            </div>
            <ol className="max-h-[64vh] divide-y overflow-auto">
              {[
                {
                  title: "Choose a source and search a lead term",
                  body: "Pick Diseases, External Cause, Drugs, or Neoplasms, and set the fiscal year. Type a lead term (for example diabetes or fracture) and select a match.",
                },
                {
                  title: "Deep search — optional refine inside the term",
                  body: "After a lead term is open, you can type in Deep Search to narrow to matching sub-terms.",
                },
                {
                  title: "Navigate the index",
                  body: "Use the A–Z buttons to jump to sections. Header Back / Forward restores the previous term. Reset All clears every encoder tab.",
                },
                {
                  title: "Validate in Tabular Validation",
                  body: "Click a code on the left to open it on the right. Use Code / Text search next to the title to search the Tabular List on its own.",
                },
                {
                  title: "Check Coding Guidelines and AHA Coding Clinic",
                  body: "The bar under Tabular Validation shows Coding Guidelines and AHA Coding Clinic for the current code. Click a tab to open the full dialog.",
                },
              ].map((step, index) => (
                <li
                  key={step.title}
                  className="grid grid-cols-[28px_1fr] items-start gap-4 px-6 py-4"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-sm font-semibold text-white">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-base font-semibold text-slate-900">{step.title}</p>
                    <p className="mt-1 text-[15px] leading-7 text-slate-700">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="border-t bg-slate-50 px-6 py-4 text-sm leading-6 text-slate-700">
              Drugs and Neoplasms skip the index — those tabs open their full table and
              the top field filters the rows directly.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const EncoderToastHost: React.FC = () => {
  const { toasts, dismissToast } = useEncoderUi();
  if (!toasts.length) return null;
  return (
    <div className="pointer-events-none fixed right-4 top-16 z-[70] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto rounded-md border px-3 py-2 text-sm shadow-lg ${
            toast.tone === "error"
              ? "border-red-200 bg-red-50 text-red-800"
              : toast.tone === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-slate-200 bg-white text-slate-800"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <p>{toast.message}</p>
            <button
              type="button"
              className="text-xs opacity-70 hover:opacity-100"
              onClick={() => dismissToast(toast.id)}
            >
              Close
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
